import React, { useEffect, useState, useRef } from "react";
import { Trash2, Plus } from "lucide-react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import Modal from "../common/CommonModal";
import useApi from "../../hooks/useApi";

const ProductionOtherCostModal = ({
  open,
  onClose,
  otherCosts,
  setOtherCosts,
  BASE_URL,
  COMPANY_CODE,
  DEPARTMENT,
  invNo,
  jobNo,
  currency,
  currencyRate,
}) => {
  // STATE & REFS
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [loadingWages, setLoadingWages] = useState(false);
  const [saving, setSaving] = useState(false);

  const { request } = useApi();
  const nrHrsInputRefs = useRef({});

  // HELPERS
  const today = () => new Date().toISOString().slice(0, 10);

  const createBlankRow = (overrides = {}) => ({
    id: Date.now() + Math.random(),
    slno: 1,
    empCode: "",
    empName: "",
    nrHrs: 0,
    nrRate: 0,
    total: 0,
    jobno: jobNo || "",
    pdate: today(),
    ...overrides,
  });

  const recalcRow = (row) => ({
    ...row,
    total: Number(row.nrHrs || 0) * Number(row.nrRate || 0),
  });

  const normalizeRows = (rows) => rows.map((r, i) => ({ ...r, slno: i + 1 }));

  const stripRowForParent = (r) => ({
    id: r.id,
    empCode: r.empCode,
    empName: r.empName,
    nrHrs: Number(r.nrHrs) || 0,
    nrRate: Number(r.nrRate) || 0,
    total: Number(r.total) || 0,
    pdate: r.pdate,
    currency: currency || "AED",
    currencyRate: Number(currencyRate) || 1,
  });

  // INITIAL LOAD
  useEffect(() => {
    if (!open) return;

    if (invNo) {
      loadWages(invNo);
    } else if (otherCosts?.length) {
      setRows(
        normalizeRows(
          otherCosts.map((r) =>
            createBlankRow({
              ...r,
              id: r.id || Date.now(),
            })
          )
        )
      );
    } else {
      setRows([createBlankRow()]);
    }
  }, [open, invNo]);

  // LOAD EMPLOYEES
  useEffect(() => {
    if (!open) return;

    const loadEmployees = async () => {
      setLoadingEmployees(true);
      try {
        const url = `${BASE_URL}/api/PersonalInfoList/${COMPANY_CODE}/ALL/YES/ALL/ALL/ALL/ALL`;
        const res = await request({ url, method: "GET", timeout: 10000 });

        setEmployees(
          (res || []).map((e) => ({
            value: e.EmpId?.trim(),
            name: e.Name?.trim(),
            label: `${e.EmpId?.trim()} - ${e.Name?.trim()}`,
          }))
        );
      } catch {
        setEmployees([]);
      } finally {
        setLoadingEmployees(false);
      }
    };

    loadEmployees();
  }, [open]);

  // FETCH HOURLY WAGE FOR SINGLE EMPLOYEE
  const fetchHourlyWage = async (empId) => {
    try {
      const url = `${BASE_URL}/api/PersonalInfoList/${COMPANY_CODE}/SINGLE/YES/${empId}/ALL/ALL/ALL`;
      const res = await request({ url, method: "GET", timeout: 10000 });
      return Number(res?.[0]?.HourlyWage || 0);
    } catch {
      return 0;
    }
  };

  // LOAD WAGES
  const loadWages = async (docNo) => {
    setLoadingWages(true);
    try {
      const url = `${BASE_URL}/api/CpaysCount/${COMPANY_CODE}/PRODWAGELIST/${docNo}/${DEPARTMENT}`;
      const res = await request({ url, method: "GET", timeout: 10000 });

      const mapped =
        res?.map((e, i) =>
          createBlankRow({
            id: Date.now() + i,
            slno: i + 1,
            empCode: e.empno?.trim(),
            empName: e.empname?.trim(),
            nrHrs: Number(e.nr_hrs || 0),
            nrRate: Number(e.nr_rate || 0),
            total: Number(e.tot_wage || 0),
            jobno: e.jobno || jobNo || "",
            pdate: e.pdate?.slice(0, 10) || today(),
          })
        ) || [];

      setRows(mapped.length ? mapped : [createBlankRow()]);
      setOtherCosts(mapped.map(stripRowForParent));
    } catch {
      setRows([createBlankRow()]);
    } finally {
      setLoadingWages(false);
    }
  };

  // ROW OPERATIONS
  const updateRows = (updater) => {
    setRows((prev) => normalizeRows(updater(prev)));
  };

  const handleChange = (id, field, value) => {
    updateRows((rows) =>
      rows.map((r) => (r.id === id ? recalcRow({ ...r, [field]: value }) : r))
    );
  };

  const handleEmpSelect = async (id, emp) => {
    if (!emp) return;

    const hourlyWage = await fetchHourlyWage(emp.value);

    updateRows((rows) =>
      rows.map((r) =>
        r.id === id
          ? recalcRow({ ...r, empCode: emp.value, empName: emp.name, nrRate: hourlyWage })
          : r
      )
    );

    setTimeout(() => nrHrsInputRefs.current[id]?.focus(), 100);
  };

  const addRow = () => updateRows((r) => [...r, createBlankRow()]);

  const deleteRow = (id) =>
    updateRows((r) => (r.length > 1 ? r.filter((x) => x.id !== id) : r));

  // SAVE - Now uses invNo prop which should come from production save response
  const handleSave = () => {
    const payloadForParent = rows
      .filter((r) => r.empCode)
      .map(stripRowForParent);

    if (!payloadForParent.length) {
      alert("Add at least one wage entry.");
      return;
    }

    // Only update parent local state. Do NOT call backend here.
    setOtherCosts(payloadForParent);
    onClose();
  };

  const totalAmount = rows.reduce((sum, r) => sum + Number(r.total || 0), 0);

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Add Other Costs (Wages)"
      header={true}
      size="xxl"
    >
      <div className="flex-1 overflow-y-auto p-6">
        {loadingWages ? (
          <div className="p-6 text-center text-gray-500">
            Loading entries...
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b-2 border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">
                      Sl No
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 min-w-[260px]">
                      Employee
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">
                      NR Hours
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">
                      NR Rate
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700">
                      Total
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {rows.map((r) => (
                    <tr
                      key={r.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {r.slno}
                      </td>
                      <td className="px-4 py-3">
                        <Autocomplete
                          options={employees}
                          getOptionLabel={(option) => option.label || ""}
                          value={
                            employees.find((e) => e.value === r.empCode) || null
                          }
                          onChange={(event, newValue) => {
                            handleEmpSelect(r.id, newValue);
                          }}
                          loading={loadingEmployees}
                          size="small"
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select employee..."
                              variant="outlined"
                            />
                          )}
                          renderOption={(props, option) => (
                            <li {...props}>
                              <div className="flex flex-col">
                                <span className="font-medium text-sm">
                                  {option.name}
                                </span>
                                <span className="text-xs text-gray-500">
                                  Code: {option.value}
                                </span>
                              </div>
                            </li>
                          )}
                          isOptionEqualToValue={(option, value) =>
                            option?.value === value?.value
                          }
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="date"
                          className="w-full px-3 py-2 text-sm border rounded-lg"
                          value={r.pdate}
                          onChange={(e) =>
                            handleChange(r.id, "pdate", e.target.value)
                          }
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          ref={(el) => (nrHrsInputRefs.current[r.id] = el)}
                          type="number"
                          className="w-full px-3 py-2 text-sm border rounded-lg"
                          value={r.nrHrs}
                          onChange={(e) =>
                            handleChange(r.id, "nrHrs", e.target.value)
                          }
                          min="0"
                          step="0.5"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          className="w-full px-3 py-2 text-sm border rounded-lg"
                          value={r.nrRate}
                          onChange={(e) =>
                            handleChange(r.id, "nrRate", e.target.value)
                          }
                          min="0"
                          step="0.01"
                        />
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                        {Number(r.total).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => deleteRow(r.id)}
                          disabled={rows.length === 1}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Delete row"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-6 flex items-center justify-between">
              <button
                onClick={addRow}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-sm"
              >
                <Plus className="h-4 w-4" /> Add Row
              </button>

              <div className="flex items-center justify-between gap-3 bg-gradient-to-r from-blue-50 to-blue-100 px-4 py-3 rounded-lg border border-blue-200">
                <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700 bg-blue-200/60 rounded-full">
                  Total Amount
                </span>

                <span className="text-2xl font-bold text-blue-700">
                  {currency || "AED"} {totalAmount.toFixed(2)}
                </span>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-xl flex items-center justify-end gap-3">
        <button
          onClick={onClose}
          className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <span className="flex items-center gap-2">
              <CircularProgress size={16} color="inherit" /> Saving...
            </span>
          ) : (
            "Save Changes"
          )}
        </button>
      </div>
    </Modal>
  );
};

export default ProductionOtherCostModal;
